import React, { createContext, useContext, useEffect, useState } from "react";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";

export const SEARCH_LUNA_KEY = "isSearchWithLunaEnabled";

interface LunaToggleContextType {
  isAISearchEnabled: boolean;
  shouldShowAISearch: boolean;
  canSeeAiSmartSearch: boolean;
  handleAISearchToggle: (value: boolean) => void;
}

const LunaToggleContext = createContext<LunaToggleContextType | null>(null);

export const LunaToggleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { canSeeAiFeatures, hasPremiumLicense } = useGetUserProperties();

  const [aiSmartSearchExperiment] =
    useGetUserExperimentSegment("ai_assisted_search");
  const isAiSmartSearchExperimentOn = aiSmartSearchExperiment === "on";
  const canSeeAiSmartSearch =
    (canSeeAiFeatures && isAiSmartSearchExperimentOn) || false;

  const [isAISearchEnabled, setIsAISearchEnabled] = useState(
    () => isLunaEnabledInStorage() || false,
  );
  const [shouldShowAISearch, setShouldShowAISearch] = useState(false);

  useEffect(() => {
    const storedValue = isLunaEnabledInStorage();
    if (storedValue !== null) {
      setIsAISearchEnabled(storedValue);
      setShouldShowAISearch(storedValue);
    } else {
      if (hasPremiumLicense) {
        setIsAISearchEnabled(true);
        setShouldShowAISearch(true);
      }
    }
  }, [hasPremiumLicense]);

  const handleAISearchToggle = (value: boolean) => {
    setIsAISearchEnabled(value);
    if (typeof window !== "undefined") {
      localStorage.setItem(SEARCH_LUNA_KEY, String(value));
    }

    setTimeout(() => {
      setShouldShowAISearch(value);
    }, 300);
  };

  return (
    <LunaToggleContext.Provider
      value={{
        isAISearchEnabled,
        shouldShowAISearch,
        canSeeAiSmartSearch,
        handleAISearchToggle,
      }}
    >
      {children}
    </LunaToggleContext.Provider>
  );
};

export const useLunaToggle = () => {
  const context = useContext(LunaToggleContext);
  if (!context) {
    throw new Error("useLunaToggle must be used within a LunaToggleProvider");
  }
  return context;
};

function isLunaEnabledInStorage(): boolean | null {
  if (typeof window !== "undefined") {
    const storedValue = localStorage.getItem(SEARCH_LUNA_KEY);
    if (storedValue !== null) {
      const parsed = storedValue === "true";
      return parsed;
    }
  }
  return false;
}
